'use client';

import { Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
    UserPlus,
    Handshake,
    FileText,
    Video,
    UserCheck,
    ArrowRight,
    ShieldCheck,
    Target,
    Zap,
    AlertCircle,
    CheckCircle2,
    Users
} from 'lucide-react';
import { GlobalBrandedLoader } from '@/doi-tac-module/components/global-branded-loader';
import { AgencyReferralCard } from '@/doi-tac-module/components/quy-trinh/agency-referral-card';
import { PostJobChoiceDialog } from '@/doi-tac-module/components/viec-lam/post-job-choice-dialog';
import React, { useState, useEffect } from 'react';
import { validateAgencyCode } from '@/doi-tac-module/actions/agency-actions';
import { agencyTranslations as at } from '@/doi-tac-module/constants/agency-translations';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { cn } from '@/doi-tac-module/lib/utils';
const tokuteiRoadmapTranslations: any = {
    hero: {
        badge: { vi: "Lộ trình Sự nghiệp Tokutei", en: "Tokutei Career Roadmap" },
        titlePrefix: { vi: "Quy trình Ứng tuyển", en: "Application Process" },
        titleSuffix: { vi: "Tokutei Việt Nam", en: "Tokutei Vietnam" },
        description: { 
            vi: "Quy trình hỗ trợ toàn diện giúp ứng viên Tokutei tại Nhật Bản tìm kiếm công việc mới, hoàn thiện hồ sơ và chuyển đổi visa nhanh chóng.",
            en: "Comprehensive support process helping Tokutei candidates in Japan find new jobs, complete profiles, and switch visas quickly."
        },
        descriptionDesktop: {
            vi: "HelloJob mang đến quy trình ứng tuyển và hỗ trợ chuyển việc chuyên nghiệp dành riêng cho ứng viên Tokutei Việt Nam tại Nhật Bản. Chúng tôi đồng hành cùng bạn từ khâu chuẩn bị hồ sơ, luyện phỏng vấn AI cho đến khi hoàn thiện thủ tục visa và bắt đầu công việc mới một cách an tâm nhất.",
            en: "HelloJob provides a professional application and job transition support process exclusively for Vietnamese Tokutei candidates in Japan. We accompany you from profile preparation and AI interview practice to visa completion and starting your new job with peace of mind."
        },
        ctaRegister: { vi: "Bắt đầu ứng tuyển", en: "Start applying" },
        ctaProcess: { vi: "Tìm hiểu lộ trình", en: "Explore roadmap" }
    },
    painPoints: {
        item1: {
            title: { vi: "Thiếu Thông Tin", en: "Lack of Information" },
            description: { 
                vi: "Khó tìm kiếm các đơn hàng Tokutei minh bạch và đúng với năng lực thực tế.",
                en: "Difficult to find transparent Tokutei jobs that match actual skills."
            }
        },
        item2: {
            title: { vi: "Thủ Tục Phức Tạp", en: "Complex Procedures" },
            description: { 
                vi: "Gặp khó khăn trong việc chuẩn bị hồ sơ và các giấy tờ chuyển đổi visa tại Nhật.",
                en: "Facing difficulties in preparing documents and visa conversion papers in Japan."
            }
        },
        item3: {
            title: { vi: "Lo Lắng Phỏng Vấn", en: "Interview Anxiety" },
            description: { 
                vi: "Thiếu tự tin khi giao tiếp và trả lời các câu hỏi chuyên môn từ nhà tuyển dụng Nhật.",
                en: "Lacking confidence when communicating and answering technical questions from Japanese employers."
            }
        }
    },
    solutions: {
        title: { vi: "Giải pháp từ HelloJob", en: "Solutions from HelloJob" },
        subtitle: { vi: "Minh Bạch – Chuyên Nghiệp – Uy Tín", en: "Transparent – Professional – Reputable" },
        items: [
            { vi: "Danh sách đơn hàng Tokutei đa dạng, được kiểm duyệt thông tin 100%.", en: "Diverse Tokutei job listings, 100% verified information." },
            { vi: "Công nghệ AI hỗ trợ luyện phỏng vấn và phát âm tiếng Nhật chuẩn xác.", en: "AI technology supports interview practice and accurate Japanese pronunciation." },
            { vi: "Hỗ trợ trọn gói thủ tục hồ sơ và tư vấn chuyển đổi visa đúng pháp luật.", en: "Full support for documentation and legal visa conversion consulting." },
            { vi: "Kết nối trực tiếp doanh nghiệp, không qua trung gian, tối ưu quyền lợi.", en: "Direct connection with businesses, no middleman, optimizing benefits." }
        ]
    },
    process: {
        title: { vi: "Quy trình 5 Bước Thành Công", en: "5-Step Success Process" },
        description: { 
            vi: "HelloJob đồng hành cùng bạn trong mọi giai đoạn của hành trình chuyển việc tại Nhật Bản.",
            en: "HelloJob accompanies you at every stage of your job transition journey in Japan."
        },
        ctaAction: { vi: "Ứng tuyển ngay", en: "Apply now" },
        steps: [
            {
                title: { vi: "1. Tạo hồ sơ năng lực", en: "1. Create Competency Profile" },
                description: { 
                    vi: "Đăng ký tài khoản và cập nhật hồ sơ chuyên nghiệp, làm nổi bật các chứng chỉ Tokutei và kinh nghiệm làm việc của bạn.",
                    en: "Register an account and update a professional profile, highlighting your Tokutei certificates and work experience."
                },
                stageLabel: { vi: "Bước 1", en: "Step 1" }
            },
            {
                title: { vi: "2. Gợi ý việc làm AI", en: "2. AI Job Suggestions" },
                description: { 
                    vi: "Hệ thống AI tự động phân tích hồ sơ và gợi ý các đơn hàng phù hợp nhất với ngành nghề và mong muốn của bạn.",
                    en: "The AI system automatically analyzes profiles and suggests the best jobs matching your industry and desires."
                },
                stageLabel: { vi: "Bước 2", en: "Step 2" }
            },
            {
                title: { vi: "3. Luyện phỏng vấn AI", en: "3. AI Interview Practice" },
                description: { 
                    vi: "Sử dụng công nghệ AI để luyện tập trả lời các câu hỏi phỏng vấn Tokutei, cải thiện phát âm và sự tự tin.",
                    en: "Use AI technology to practice answering Tokutei interview questions, improving pronunciation and confidence."
                },
                stageLabel: { vi: "Bước 3", en: "Step 3" }
            },
            {
                title: { vi: "4. Phỏng vấn & Nhận Naitei", en: "4. Interview & Naitei" },
                description: { 
                    vi: "Tham gia phỏng vấn trực tiếp với nhà tuyển dụng Nhật Bản và nhận thông báo kết quả (Naitei) ngay trên hệ thống.",
                    en: "Join direct interviews with Japanese employers and receive result notifications (Naitei) right on the system."
                },
                stageLabel: { vi: "Bước 4", en: "Step 4" }
            },
            {
                title: { vi: "5. Hoàn thiện Visa", en: "5. Visa Completion" },
                description: { 
                    vi: "Hỗ trợ hoàn thiện các thủ tục pháp lý và chuyển đổi visa Tokutei để bạn bắt đầu công việc mới một cách an tâm nhất.",
                    en: "Support completing legal procedures and Tokutei visa conversion to start your new job with total peace of mind."
                },
                stageLabel: { vi: "Bước 5", en: "Step 5" }
            }
        ]
    },
    finalCta: {
        title: { vi: "Sẵn sàng cho khởi đầu mới?", en: "Ready for a new start?" },
        description: { 
            vi: "Hàng ngàn cơ hội việc làm Tokutei tốt nhất đang chờ đón bạn. Hãy để HelloJob giúp bạn chinh phục mục tiêu sự nghiệp.",
            en: "Thousands of the best Tokutei job opportunities are waiting for you. Let HelloJob help you conquer your career goals."
        },
        trustLabel: { vi: "NỀN TẢNG TIN CẬY CHO CỘNG ĐỒNG TOKUTEI", en: "TRUSTED PLATFORM FOR TOKUTEI COMMUNITY" }
    }
};

const stepsData = [
    {
        id: 1,
        icon: UserPlus,
        color: "bg-[#19A6DF]/10 text-[#19A6DF]",
        image: "/img/PHONGVAN/phongvan01.jpg",
        dataAiHint: "modern registration form on smartphone"
    },
    {
        id: 2,
        icon: Target,
        color: "bg-[#F2B92A]/10 text-[#F2B92A]",
        image: "/img/BANGLADESH/TRANGUNGVIEN/lotrinh2.jpg",
        dataAiHint: "AI matching jobs"
    },
    {
        id: 3,
        icon: Video,
        color: "bg-[#AFC536]/10 text-[#AFC536]",
        image: "/img/PHONGVAN/phongvan03.jpg",
        dataAiHint: "AI interview practice"
    },
    {
        id: 4,
        icon: Handshake,
        color: "bg-[#0D8DC8]/10 text-[#0D8DC8]",
        image: "/img/PHONGVAN/phongvan05.jpg",
        dataAiHint: "successful job interview"
    },
    {
        id: 5,
        icon: ShieldCheck,
        color: "bg-[#19A6DF]/10 text-[#19A6DF]",
        image: "/img/visa-tokutei-gino-2.jpg",
        dataAiHint: "visa documents and residency status"
    }
];

const painPointsData = [
    { icon: AlertCircle, key: 'item1' },
    { icon: Zap, key: 'item2' },
    { icon: Target, key: 'item3' }
];

function RoadmapContent() {
    const { language } = useLanguage();
    const router = useRouter();
    const searchParams = useSearchParams();
    const refId = searchParams.get('ref');
    
    const { user, isLoggedIn } = useAuth();
    const [isPostJobDialogOpen, setIsPostJobDialogOpen] = useState(false);
    const t = tokuteiRoadmapTranslations;

    const [isValidatingRef, setIsValidatingRef] = useState(!!refId);
    const [showInvalidRefPopup, setShowInvalidRefPopup] = useState(false);

    // Redirect về trang tạo nội dung phỏng vấn hoặc tìm việc
    const redirectTarget = encodeURIComponent('/ung-vien');
    const authUrl = refId
        ? `/xac-thuc?ref=${refId}&redirect=${redirectTarget}`
        : `/xac-thuc?redirect=${redirectTarget}`;

    let mainBtnText = t.hero.ctaRegister[language];
    let mainBtnLink = authUrl;
    let heroDescription: React.ReactNode = t.hero.descriptionDesktop?.[language] || t.hero.description[language];

    if (isLoggedIn) {
        mainBtnText = "Xem danh sách việc làm";
        mainBtnLink = "/ung-vien";
        heroDescription = (
            <>Chào mừng bạn quay lại! Hãy tiếp tục <span className="font-bold underline decoration-[#19A6DF] underline-offset-2">khám phá các đơn hàng Tokutei</span> mới nhất và hoàn thiện lộ trình sự nghiệp của mình.</>
        );
    }

    // Validate referral code on mount
    useEffect(() => {
        const checkRef = async () => {
            if (!refId) return;
            
            setIsValidatingRef(true);
            try {
                const res = await validateAgencyCode(refId);
                if (!res.success) {
                    setShowInvalidRefPopup(true);
                }
            } catch (err) {
                console.error("Failed to validate ref code on roadmap:", err);
                setShowInvalidRefPopup(true);
            } finally {
                setIsValidatingRef(false);
            }
        };
        
        checkRef();
    }, [refId]);

    return (
        <div className="flex flex-col w-full min-h-screen bg-slate-50/50">
            {/* Hero Section */}
            <section className="relative w-full pt-20 md:pt-28 pb-16 bg-blue-600 bg-gradient-to-r from-blue-600 to-sky-500 overflow-hidden">
                <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,rgba(255,255,255,0),rgba(255,255,255,0.5))]" />
                <div className="container relative mx-auto px-4 md:px-6 text-center text-white">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1 text-sm font-bold backdrop-blur-md border border-white/30 mb-8 mx-auto">
                        <Users className="h-4 w-4" />
                        {t.hero.badge[language]}
                    </div>
                    <h1 className="text-4xl md:text-5xl font-headline font-bold mb-8 leading-snug md:leading-tight">
                        {t.hero.titlePrefix[language]} <br /> <span className="text-[#F2B92A]">{t.hero.titleSuffix[language]}</span>
                    </h1>
                    {/* Mobile description */}
                    <p className="md:hidden text-base text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed">
                        {heroDescription}
                    </p>
                    {/* Desktop description (3 lines) */}
                    <p className="hidden md:block text-lg text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed">
                        {heroDescription}
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Button size="lg" className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-medium h-12 px-8 text-lg rounded-xl shadow-lg shadow-orange-950/20" asChild>
                            <Link href={mainBtnLink}>{mainBtnText}</Link>
                        </Button>
                        <Button variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm h-12 px-8 text-lg rounded-xl font-medium" asChild>
                            <Link href="#process">{t.hero.ctaProcess[language]}</Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Pain Points Section */}
            <section className="w-full py-20 -mt-10 relative z-10">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {painPointsData.map((point, index) => {
                            const content = (t.painPoints as any)[point.key];
                            return (
                                <Card key={index} className="border-none shadow-xl shadow-slate-200/50 rounded-2xl overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
                                    <CardContent className="p-8">
                                        <div className="h-14 w-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mb-6 group-hover:bg-red-500 group-hover:text-white transition-colors duration-300">
                                            <point.icon className="h-7 w-7" />
                                        </div>
                                        <h3 className="text-2xl font-headline font-bold mb-4 text-slate-800">
                                            {content.title[language]}
                                        </h3>
                                        <p className="text-slate-600 leading-relaxed">
                                            {content.description[language]}
                                        </p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CVP Section */}
            <section className="w-full py-20 bg-white border-y">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-20">
                        <div className="lg:w-1/2 w-full">
                            <div className="relative aspect-[3/2] rounded-3xl overflow-hidden shadow-2xl bg-white">
                                <Image
                                    src="/img/BANGLADESH/TRANGUNGVIEN/lotrinh0.jpg"
                                    alt="Tokutei Support"
                                    fill
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                    className="object-contain"
                                />
                            </div>
                        </div>
                        <div className="lg:w-1/2">
                            <h2 className="text-3xl md:text-4xl font-headline font-bold mb-8 text-slate-900 leading-tight">
                                {t.solutions.title[language]} <br /> <span className="text-[#19A6DF]">{t.solutions.subtitle[language]}</span>
                            </h2>
                            <div className="space-y-6">
                                {t.solutions.items.map((item: any, i: number) => (
                                    <div key={i} className="flex items-start gap-4">
                                        <div className="mt-1 h-6 w-6 rounded-full bg-green-50 text-green-600 flex flex-shrink-0 items-center justify-center">
                                            <CheckCircle2 className="h-4 w-4" />
                                        </div>
                                        <p className="text-lg text-slate-700">{item[language]}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Detailed 5-Step Process */}
            <section id="process" className="w-full py-32 bg-background">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="max-w-3xl mx-auto text-center mb-24">
                        <h2 className="text-3xl md:text-4xl font-headline font-bold mb-6 text-slate-900">
                            {t.process.title[language]}
                        </h2>
                        <p className="text-xl text-slate-500">
                            {t.process.description[language]}
                        </p>
                    </div>

                    <div className="space-y-32">
                        {stepsData.map((step, index) => {
                            const stepContent = t.process.steps[index];
                            return (
                                <div key={step.id} className={`flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-24 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                                    <div className="lg:w-1/2 w-full">
                                        <div className="relative group">
                                            <div className={`absolute -inset-4 rounded-[2rem] ${step.color} blur-2xl opacity-20 group-hover:opacity-40 transition-opacity`} />
                                            <div className="relative aspect-[3/2] rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-white">
                                                <Image
                                                    src={step.image}
                                                    alt={stepContent.title[language]}
                                                    fill
                                                    sizes="(max-width: 768px) 100vw, 50vw"
                                                    priority={index === 0}
                                                    className="object-contain transition-transform duration-500 group-hover:scale-105 will-change-transform"
                                                    data-ai-hint={step.dataAiHint}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="lg:w-1/2">
                                        <div className={`inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl text-sm font-bold tracking-wider ${step.color}`}>
                                            <step.icon className="h-5 w-5" />
                                            {stepContent.stageLabel[language]}
                                        </div>
                                        <h3 className="text-2xl md:text-3xl font-headline font-bold mb-6 text-slate-900 leading-tight flex items-center flex-wrap gap-3">
                                            {stepContent.title[language]}
                                            {isLoggedIn && step.id === 1 && (
                                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1.5 py-1 text-sm rounded-xl">
                                                    <CheckCircle2 className="w-4 h-4 text-green-500" /> Đã hoàn thành
                                                </Badge>
                                            )}
                                        </h3>
                                        <p className="text-xl text-slate-600 leading-relaxed mb-10">
                                            {stepContent.description[language]}
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            <Button className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-medium h-12 px-8 text-lg rounded-xl shadow-lg shadow-orange-950/10 group" asChild>
                                                <Link href={mainBtnLink}>
                                                    {isLoggedIn ? mainBtnText : t.process.ctaAction[language]}
                                                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                                </Link>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="w-full py-24 bg-slate-950 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#19A6DF]/20 rounded-full blur-[120px] -mr-64 -mt-64" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#F2B92A]/10 rounded-full blur-[120px] -ml-64 -mb-64" />

                <div className="container relative mx-auto px-4 md:px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-headline font-bold mb-8">
                        {t.finalCta.title[language]}
                    </h2>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12">
                        {t.finalCta.description[language]}
                    </p>
                    <div className="flex justify-center gap-6">
                        <Button size="lg" className="bg-[#19A6DF] hover:bg-[#19A6DF]/90 text-white font-medium h-12 px-10 text-lg rounded-2xl shadow-xl shadow-blue-900/40" asChild>
                            <Link href={mainBtnLink}>{mainBtnText}</Link>
                        </Button>
                    </div>

                    <AgencyReferralCard />

                    <div className="mt-16 flex items-center justify-center gap-8 grayscale opacity-50">
                        <ShieldCheck className="h-10 w-10" />
                        <span className="text-sm font-medium tracking-[0.2em]">
                            {t.finalCta.trustLabel[language]}
                        </span>
                    </div>
                </div>
            </section>

            {/* Popup thông báo mã giới thiệu không hợp lệ */}
            <AlertDialog open={showInvalidRefPopup}>
                <AlertDialogContent className="max-w-md rounded-3xl">
                    <AlertDialogHeader>
                        <div className="mx-auto bg-red-100 rounded-full p-3 w-fit mb-4">
                            <ShieldCheck className="h-8 w-8 text-red-600" />
                        </div>
                        <AlertDialogTitle className="text-2xl font-bold text-center font-headline">
                            {at.sso.invalidRef.title[language]}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-center text-slate-600 text-lg">
                            {at.sso.invalidRef.desc[language]}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-col gap-2 mt-6">
                        <AlertDialogAction 
                            onClick={() => router.push('/')}
                            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl text-base font-semibold"
                        >
                            {at.sso.invalidRef.button[language]}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Loading Overlay khi đang validate mã ref */}
            {isValidatingRef && <GlobalBrandedLoader forceShow={true} />}

            {/* Post Job Choice Dialog */}
            <PostJobChoiceDialog open={isPostJobDialogOpen} onOpenChange={setIsPostJobDialogOpen} />
        </div>
    );
}


export default function QuyTrinhPage() {
    return (
        <Suspense fallback={<GlobalBrandedLoader forceShow={true} />}>
            <RoadmapContent />
        </Suspense>
    );
}

